import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addBetToBetSlip,
  removeBetFromBetSlip
} from 'stores/betSlip';
import { selectEventById } from 'stores/outrights';
import usePrevious from '../../../toolkit/useHooks/usePrevious';

// Build the synthetic "match" shape consumed by the betSlip reducer + downstream
// transformers. checkIfLiveEvent(match) reads match.markets[].producer; outrights
// always use producer=1 so it returns false — exactly what we want for non-live
// long-term events. isSeason=true tells oddsTransformer this is an outright.
function buildOutrightMatch(event) {
  if (!event?.se) return null;
  const se = event.se;
  return {
    providerId: se.id,
    gameDate: se.schedule_StartTime,
    schedule_StartTime: se.schedule_StartTime,
    schedule_EndTime: se.schedule_EndTime,
    isSeason: true,
    isMatch: false,
    isOutright: true,
    sport: se.sportId,
    sportType: se.sportId,
    eventName: se.eventName,
    homeName: se.eventName,
    awayName: '',
    league: se.seasonInfo?.tournamentName || '',
    category: se.seasonInfo?.categoryName || '',
    seasonInfo: se.seasonInfo || null,
    markets: event.markets || []
  };
}

function OutrightOddButton({ event, market, outcome, label, name, fullWidth = false }) {
  const dispatch = useDispatch();
  const bets = useSelector((state) => state.betSlip.bets);

  // Always read the latest event/market/outcome from the store so odds updates
  // propagate (mirrors how OddButton derives outcome from match.markets).
  const liveEvent = useSelector((state) =>
    event?.se?.id ? selectEventById(state, event.se.id) : null
  );
  const sourceEvent = liveEvent || event;
  const liveMarket = useMemo(() => {
    if (!sourceEvent?.markets || !market?.stringId) return market;
    return sourceEvent.markets.find((m) => m.stringId === market.stringId) || market;
  }, [sourceEvent, market]);
  const liveOutcome = useMemo(() => {
    if (!liveMarket?.outcomes || !outcome?.id) return outcome;
    return liveMarket.outcomes.find((o) => o.id === outcome.id) || outcome;
  }, [liveMarket, outcome]);

  const providerId = sourceEvent?.se?.id;
  const marketStringId = liveMarket?.stringId;
  const outcomeId = liveOutcome?.id;
  const odds = typeof liveOutcome?.odds === 'number' ? liveOutcome.odds : null;
  const disabled = liveOutcome?.enabled === false || odds == null || odds <= 1;

  const isSelected = useMemo(
    () =>
      bets.some(
        (b) =>
          b.providerId === providerId &&
          b.marketStringId === marketStringId &&
          b.outcomeId === outcomeId &&
          !b.isBetBuilder
      ),
    [bets, providerId, marketStringId, outcomeId]
  );

  // Brief flash on odds movement (match OddButton behavior).
  const prevOdds = usePrevious(odds);
  const [oddsClass, setOddsClass] = useState('');
  const flashTimer = useRef(null);
  useEffect(() => {
    if (prevOdds && odds && odds !== prevOdds) {
      setOddsClass(odds > prevOdds ? 'odd-increased' : 'odd-decreased');
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setOddsClass(''), 5000);
    }
  }, [odds, prevOdds]);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );

  const onClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      if (!providerId || !marketStringId || !outcomeId) return;

      if (isSelected) {
        dispatch(
          removeBetFromBetSlip({
            providerId,
            marketStringId,
            outcomeId
          })
        );
        return;
      }

      const matchPayload = buildOutrightMatch(sourceEvent);
      dispatch(
        addBetToBetSlip({
          providerId,
          marketStringId,
          marketId: liveMarket.id,
          outcomeId,
          outcomeName: liveOutcome.name,
          odds,
          match: matchPayload
        })
      );
    },
    [dispatch, disabled, isSelected, providerId, marketStringId, outcomeId, liveMarket?.id, liveOutcome?.name, odds, sourceEvent]
  );

  const oddsText = odds != null ? Number(odds).toFixed(2) : '-';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`border-tertiary rounded px-1 d-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-8 align-items-center ${name ? 'justify-content-between' : 'justify-content-center'} ${oddsClass}`.trim()}
      style={{
        backgroundColor: isSelected ? '#FFCC00' : 'transparent',
        color: isSelected ? '#000' : 'var(--bs-body-color)',
        fontVariantNumeric: 'tabular-nums',
        opacity: disabled ? 0.5 : 1,
        gap: 6,
        textAlign: 'left'
      }}
    >
      {label && (
        <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.85 }}>
          {label}
        </span>
      )}
      {name && (
        <span
          className="fw-semibold"
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'normal'
          }}
        >
          {name}
        </span>
      )}
      <span style={{ flexShrink: 0 }}>{oddsText}</span>
    </button>
  );
}

export default OutrightOddButton;
